import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import {
  Inquiry,
  InquiryPricing,
  InquiryRow,
  InquirySource,
} from 'src/models/inquiry.model';

export interface CreateProposalParams {
  address_id: number;
  check_in_date: string;
  check_out_date: string;
  nights_total_amount: number;
  security_deposit_amount?: number;
  guest_name?: string;
  party?: { total_adults?: number; total_children?: number };
  note?: string;
  expires_at?: string;
}

const SOURCE_LABELS: Record<InquirySource | 'tidy', string> = {
  direct_booking: 'Direct Booking',
  tidy: 'Direct Booking',
  airbnb: 'Airbnb',
  vrbo: 'VRBO',
  booking_com: 'Booking.com',
  other: 'Other',
};

// The channel localizes each host-payout line's `description` to the listing's
// locale (e.g. a BRL listing returns pt-BR text), which leaks into an English
// app. Map the known fee/tax/discount phrases (pt + en keywords) to canonical
// English labels; the nightly line is rebuilt from structured data separately.
const BREAKDOWN_LABELS: { keywords: string[]; label: string }[] = [
  { keywords: ['serviço', 'service'], label: 'Service fee' },
  { keywords: ['limpeza', 'cleaning'], label: 'Cleaning fee' },
  { keywords: ['desconto', 'discount'], label: 'Discount' },
  { keywords: ['imposto', 'occupancy', 'tax'], label: 'Taxes' },
];

/**
 * Host-side Inquiries across channels.
 *
 * Inquiries are a first-class gotidy resource (`/inquiries`) — distinct from
 * reservations. Airbnb uses pre-approval (a 24h guest booking window), while
 * direct-booking inquiries can advertise a one-step approval action.
 */
@Injectable({ providedIn: 'root' })
export class InquiriesService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api_url}api/v1/customer/inquiries`;

  /**
   * GET /inquiries (address hash for display). Defaults to guest-created
   * inquiries; the `created_by` filter selects the customer-created ones instead.
   */
  getInquiries(filters: { createdBy?: 'customer' | 'guest'; addressId?: number } = {}): Observable<Inquiry[]> {
    const params: Record<string, string | number> = { per_page: 100, includes: 'address' };
    if (filters.createdBy) { params['created_by'] = filters.createdBy; }
    if (filters.addressId) { params['address_id'] = filters.addressId; }
    return this.http
      .get<InquiryRow[]>(this.baseUrl, { params })
      .pipe(map((rows) => rows.map((row) => this.toInquiry(row))));
  }

  /** POST /inquiries — a proposal: the host prices a stay and shares its link. */
  createProposal(params: CreateProposalParams): Observable<Inquiry> {
    return this.http
      .post<InquiryRow>(this.baseUrl, params)
      .pipe(map((row) => this.toInquiry(row)));
  }

  /**
   * GET /inquiries/:id — a single inquiry (with the address hash for display).
   * Backs the schedule "View Details" modal when the card is an inquiry.
   */
  getInquiryById(id: number): Observable<Inquiry> {
    return this.http
      .get<InquiryRow>(`${this.baseUrl}/${id}?includes=address,card`)
      .pipe(map((row) => this.toInquiry(row)));
  }

  /** GET /inquiries/:platform/decline-reasons — the platform's decline-reason enum. */
  getDeclineReasons(platform: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/${platform}/decline-reasons`);
  }

  /**
   * POST /inquiries/:id/preapprove
   * Airbnb pre-approval: invites the guest to book at the inquiry's
   * dates/price within 24h. Expires back to a plain inquiry if unused.
   *
   * The endpoint responds 204 No Content — success/error is all the caller needs.
   */
  preapproveInquiry(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/preapprove`, {});
  }

  /**
   * POST /inquiries/:id/approve — tidy (direct-booking) only, no body.
   * In ONE call the backend re-checks availability, creates the guest
   * reservation, and CHARGES the guest's saved card for the whole
   * amounts_breakdown (stay + security deposit). 200 returns the full updated
   * inquiry. Errors ({error:{message,code,data:{reason}}}):
   * - 403: cannot be approved / has expired.
   * - 422: dates gone (reason dates_unavailable | min_stay_not_met |
   *   night_unpriced) — nothing charged; decline is the only action left.
   * - 402: card refused — everything rolled back, but the inquiry now carries
   *   pending_requirements.card, stays pending (+24h), the guest was emailed a
   *   fix-card link, and approve remains available as a retry.
   */
  approveInquiry(id: number): Observable<Inquiry> {
    return this.http
      .post<InquiryRow>(`${this.baseUrl}/${id}/approve`, {})
      .pipe(map((row) => this.toInquiry(row)));
  }

  /**
   * POST /inquiries/:id/decline
   * The platform-specific payload rides a `data` object. Airbnb: `declineReason`
   * comes from getDeclineReasons(platform); `messageToGuest` is a visible chat
   * message; `messageToPlatform` is an internal note the platform requires
   * (min 30 chars) for some reasons, never guest-facing. Tidy: nothing inside
   * `data` is validated — an optional free-text `declineReason` is passed into
   * the guest's decline email, and `{data:{}}` is fine.
   */
  declineInquiry(
    id: number,
    declineReason?: string,
    messageToGuest?: string,
    messageToPlatform?: string
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/decline`, {
      data: {
        ...(declineReason ? { decline_reason: declineReason } : {}),
        ...(messageToGuest ? { message_to_guest: messageToGuest } : {}),
        ...(messageToPlatform ? { message_to_platform: messageToPlatform } : {}),
      },
    });
  }

  private toInquiry(row: InquiryRow): Inquiry {
    const source = this.normalizeSource(row.platform);
    const propertyName = row.address?.address1 ?? 'Property';
    // Tidy sends the full guest contact; channels only ever send guest_name.
    const contactName = [row.guest?.first_name, row.guest?.last_name]
      .filter(Boolean)
      .join(' ');
    const cardRequirement = row.pending_requirements?.card;

    return {
      id: row.id,
      status: row.status ?? 'pending',
      source,
      platform: typeof row.platform === 'string' ? row.platform : 'other',
      source_name: SOURCE_LABELS[source] ?? 'Other',
      address_id: row.address_id,
      property_name: propertyName,
      property_location: row.address?.city ?? undefined,
      guest: {
        name: contactName || row.guest_name || 'Guest',
        email: row.guest?.email ?? undefined,
        phone: row.guest?.phone ?? undefined,
      },
      check_in_date: row.check_in_date ? row.check_in_date.slice(0, 10) : null,
      check_out_date: row.check_out_date ? row.check_out_date.slice(0, 10) : null,
      total_guests: this.toTotalGuests(row),
      message: row.message || undefined,
      pricing: this.toPricing(row),
      platform_url: row.platform_inquiry_url ?? undefined,
      available_actions: row.available_actions ?? [],
      // Decline reasons are fetched per-platform via getDeclineReasons(); the
      // page falls back to its own label set when this is empty.
      decline_reasons: [],
      total_amount_cents:
        typeof row.total_amount === 'number' ? row.total_amount : undefined,
      pending_requirements: cardRequirement
        ? { card: { reason: cardRequirement.reason, failed_at: cardRequirement.failed_at } }
        : {},
      // A tidy inquiry always has a saved card (one is required at creation);
      // the list fetch skips ?includes=card, so presence comes from the
      // platform there and last4 only from the card-including detail fetch.
      card_on_file: row.platform === 'tidy' || !!row.card?.last4,
      card_last4: row.card?.last4 ?? undefined,
      expires_at: row.expires_at ?? undefined,
      uuid: row.uuid,
      created_by: row.created_by ?? 'guest',
      customer_note: row.customer_note ?? undefined,
      guest_reservation_id: row.guest_reservation?.id,
    };
  }

  // Tidy inquiries carry the party as separate counts with total_guests left
  // null, so sum adults + children (infants and pets don't count as guests).
  private toTotalGuests(row: InquiryRow): number | undefined {
    // Collapse the API's nulls to undefined first, so one strict comparison
    // covers both a null count and a missing one — a channel that omits the
    // key rather than sending null still has to reach the adults + children
    // sum below.
    const total = row.total_guests ?? undefined;
    const adults = row.total_adults ?? undefined;
    const children = row.total_children ?? undefined;
    if (total !== undefined) {return total;}
    if (adults === undefined && children === undefined) {return undefined;}
    return (adults ?? 0) + (children ?? 0);
  }


  // Captured money → the page's pricing contract. Channels: the host payout /
  // Total is the SUM of the { description, amount } line items. Tidy: the items
  // are TYPED ({ type, value }) and the Total is the backend's `total_amount`
  // (the exact amount /approve charges). No breakdown → no pricing. Nights come
  // from the dates, falling back to the channel's own accommodation line.
  private toPricing(row: InquiryRow): InquiryPricing | undefined {
    const items = row.amounts_breakdown ?? [];
    if (!items.length || !row.currency) {
      return undefined;
    }
    const currency = row.currency;
    const nights = this.nightsFor(row);

    // Tidy's typed shape: every item carries `type` + `value` (cents).
    if (items.every((item) => typeof item.type === 'string' && typeof item.value === 'number')) {
      const breakdown = items.map((item) => ({
        description: this.typedBreakdownLabel(item.type!, nights),
        amount_cents: item.value!,
      }));
      const nightsTotal = items.find((item) => item.type === 'nights_total_amount')?.value;
      const deposit = items.find((item) => item.type === 'security_deposit_amount')?.value;
      const total =
        typeof row.total_amount === 'number'
          ? row.total_amount
          : breakdown.reduce((sum, item) => sum + item.amount_cents, 0);
      return {
        currency,
        nights,
        subtotal_cents: nightsTotal,
        security_deposit_cents: deposit,
        total_cents: total,
        breakdown,
      };
    }

    const breakdown = items.map((item) => ({
      description: this.localizeBreakdownLabel(item.description ?? '', item.amount ?? 0, nights, currency),
      amount_cents: item.amount ?? 0,
    }));
    const total = breakdown.reduce((sum, item) => sum + item.amount_cents, 0);
    return {
      currency,
      nights,
      total_cents: total,
      breakdown,
    };
  }

  // Labels for tidy's typed breakdown items ("4 nights", "Security deposit").
  private typedBreakdownLabel(type: string, nights: number | null): string {
    switch (type) {
      case 'nights_total_amount':
        return nights && nights > 0 ? `${nights} night${nights === 1 ? '' : 's'}` : 'Nights';
      case 'security_deposit_amount':
        return 'Security deposit';
      default:
        // Forward-compat: humanize an unknown type rather than drop the line.
        return type.replace(/_amount$/, '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
    }
  }

  // Stay length, or null when it genuinely can't be known. Dates are the source
  // of truth, but a dateless inquiry still carries the channel's own
  // accommodation line ("4 nights x $61.75"), whose leading integer is the night
  // count — so a thread that lost its dates in capture can still price honestly.
  private nightsFor(row: InquiryRow): number | null {
    const nightMs = Date.parse(row.check_out_date ?? '') - Date.parse(row.check_in_date ?? '');
    const fromDates = Math.round(nightMs / 86_400_000);
    if (Number.isFinite(fromDates) && fromDates > 0) {
      return fromDates;
    }
    return this.nightsFromBreakdown(row.amounts_breakdown ?? []);
  }

  // The leading integer of the accommodation line. Locale-safe: the channel
  // localizes the noun ("4 nights" / "4 noites") but not the numeral or the ×.
  // Tidy's typed items carry no description, so they simply never match.
  private nightsFromBreakdown(
    breakdown: Array<{ description?: string; amount?: number }>
  ): number | null {
    for (const item of breakdown) {
      const description = (item.description ?? '').trim();
      if ((item.amount ?? 0) <= 0 || !/[x×*]/i.test(description)) {
        continue;
      }
      const nights = Number(/^(\d+)\s/.exec(description)?.[1]);
      if (Number.isInteger(nights) && nights > 0) {
        return nights;
      }
    }
    return null;
  }

  // Render a host-payout line label in the app's language. Amounts/currency are
  // never touched — only the descriptive text. On a Portuguese app the channel's
  // own pt-BR text is already right, so leave it untouched.
  private localizeBreakdownLabel(
    description: string,
    amountCents: number,
    nights: number | null,
    currency: string
  ): string {
    const raw = (description ?? '').trim();
    const language = (localStorage.getItem('language') ?? 'en').toLowerCase();
    if (language.startsWith('pt')) {
      return raw;
    }
    const lower = raw.toLowerCase();
    // Accommodation line ("N noites x <price>"): rebuild in English from the
    // structured nights + amount so no translated string survives. With an
    // unknown night count the rebuild would invent a rate, so keep the
    // channel's own line — a foreign-language label beats a wrong number.
    if (/[x×*]/.test(lower) && /\d/.test(lower) && amountCents > 0 && nights !== null && nights > 0) {
      const perNight = Math.round(amountCents / nights);
      return `${nights} night${nights === 1 ? '' : 's'} × ${this.money(perNight, currency)}`;
    }
    for (const { keywords, label } of BREAKDOWN_LABELS) {
      if (keywords.some((keyword) => lower.includes(keyword))) {
        return label;
      }
    }
    // Unknown label — show it as-is rather than drop the line.
    return raw;
  }

  private money(cents: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  private normalizeSource(platform: unknown): InquirySource {
    switch (platform) {
      case 'airbnb':
      case 'vrbo':
      case 'booking_com':
      case 'direct_booking':
        return platform;
      case 'booking':
        return 'booking_com';
      // Direct-booking inquiries arrive as platform 'tidy'.
      case 'tidy':
        return 'direct_booking';
      default:
        return 'other';
    }
  }
}
